import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { formatDateTime, getEventTypeLabel, getEventOriginLabel } from "@/lib/display-labels";
import type { MachineEventSummary } from "@/features/lines/hooks";
import { MachineLabel } from "./MachineLabel";

const REASON_STATUS_LABEL: Record<MachineEventSummary["reasonStatus"], string> = {
  PENDING: "Pendente",
  JUSTIFIED: "Justificado",
};

interface Props {
  event: MachineEventSummary | null;
  open: boolean;
  onClose: () => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2 px-2 py-1.5">
      <dt className="text-text-secondary">{label}</dt>
      <dd className="text-right font-mono text-text-primary">{value}</dd>
    </div>
  );
}

function formatDuration(min: number | null): string {
  if (min === null) return "—";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export function EventDetailDialog({ event, open, onClose }: Props): React.ReactElement | null {
  if (!event) return null;
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] animate-fade-in bg-background/80" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[61] w-full max-w-md -translate-x-1/2 -translate-y-1/2 animate-scale-in rounded-lg border bg-card p-5 shadow-lg focus:outline-none">
          <div className="mb-3 flex items-start justify-between gap-2">
            <Dialog.Title className="text-base font-semibold text-text-primary">
              {getEventTypeLabel(event.type)}
            </Dialog.Title>
            <Dialog.Close className="rounded p-1 text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]">
              <X className="h-4 w-4" />
              <span className="sr-only">Fechar</span>
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            Detalhes auditáveis do evento de máquina selecionado.
          </Dialog.Description>

          <dl className="bg-bg-muted-rd/40 divide-y divide-border-subtle rounded border text-[12px]">
            <Row label="Tipo" value={getEventTypeLabel(event.type)} />
            <Row label="Origem" value={getEventOriginLabel(event.origin)} />
            <Row
              label="Máquina"
              value={<MachineLabel name={event.machineName} code={event.machineCode} />}
            />
            <Row label="Motivo" value={event.reason ?? "—"} />
            <Row
              label="Status do motivo"
              value={REASON_STATUS_LABEL[event.reasonStatus] ?? event.reasonStatus}
            />
            {event.justifiedAt ? (
              <Row label="Justificado em" value={formatDateTime(event.justifiedAt)} />
            ) : null}
            <Row label="Início" value={formatDateTime(event.startAt)} />
            <Row label="Fim" value={event.endAt ? formatDateTime(event.endAt) : "—"} />
            <Row label="Duração" value={formatDuration(event.durationMinutes)} />
            <Row label="ID" value={<span className="font-mono text-[11px]">{event.id}</span>} />
          </dl>

          {/* Histórico canônico (quem/quando) na trilha de auditoria. */}
          <div className="mt-3 text-right">
            <Link
              to="/audit"
              onClick={onClose}
              className="rounded text-[12px] text-brand-700 hover:underline focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
            >
              Ver no histórico de auditoria
            </Link>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
